import React, { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { searchProspects } from '../../../queries/prospects';
import { textFieldSx } from './constants';

const useDebouncedValue = (value, delayMs = 250) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};

const ProspectNameAutocomplete = ({ value, onInputChange, onSelectProspect, sx = textFieldSx }) => {
  const debouncedQuery = useDebouncedValue(value, 250);
  const canSearch = debouncedQuery.trim().length >= 2;

  // Fetch whenever the typed name is long enough — do NOT gate on dropdown open.
  // freeSolo only opens the popup when options exist; requiring `open` caused a deadlock.
  const { data: options = [], isFetching } = useQuery(
    searchProspects({
      q: debouncedQuery,
      enabled: canSearch,
    }),
  );

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={canSearch && isFetching}
      filterOptions={(x) => x}
      getOptionKey={(option) => (typeof option === 'string' ? option : option._id)}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.fullName || '';
      }}
      isOptionEqualToValue={(option, selected) =>
        typeof option === 'object' &&
        typeof selected === 'object' &&
        option?._id === selected?._id
      }
      inputValue={value}
      onInputChange={(_event, nextValue, reason) => {
        if (reason !== 'input' && reason !== 'clear') return;
        onInputChange(nextValue);
      }}
      onChange={(_event, selected) => {
        if (selected && typeof selected === 'object') {
          onSelectProspect(selected);
        }
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key || option._id} {...optionProps}>
            {option.fullName}
            {option.company ? ` — ${option.company}` : ''}
          </li>
        );
      }}
      noOptionsText={
        !canSearch ? 'Type at least 2 characters to search your prospects' : 'No matching prospects'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size='small'
          label='Full name'
          placeholder='Jane Smith'
          sx={sx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {canSearch && isFetching ? <CircularProgress color='inherit' size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProspectNameAutocomplete;
